import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Calendar, CalendarEvent, CalendarEventCategory, CalendarViewMode, DateRange } from '@ngx-seatback/calendar';
import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import {
  API,
  CalendarDTO,
  CalendarEntryDTO,
  CalendarEntryFullDTO,
  CalendarEntryMetaDataDTO,
  CalendarEntryTypeDTO,
  CalendarFullDTO,
  CommercialDTO,
  CullDTO,
  FilterableInventoryRequestDTO,
  InventoryAggregationDTO,
  Pagination,
  PlayerCullDTO,
  ScheduledCampaignDTO,
} from 'src/app/services/api/api';
import { SbLocalDate, SbLocalDateTime, SbLocalTime } from 'src/app/services/api/api-date-time';
import { ErrorService } from 'src/app/services/error.service';
import { PermissionService } from 'src/app/services/permission.service';
import { UtilService } from 'src/app/services/util.service';
import { MsOptions } from '../../../services/global/global-interfaces';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { CampaignsListComponent } from '../../campaigns/bookings/campaigns-list/campaigns-list.component';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { CommercialReservationService } from '../../../services/commercial-reservation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'app-reservation',
  templateUrl: './reservation.component.html',
  styleUrls: ['./reservation.component.scss'],
  standalone: false,
})
export class ReservationComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  pagination = new BehaviorSubject<Pagination>({ page: 1, count: 2000, pages: -1, resultcount: -1 });
  commercials: CommercialDTO[];
  events: CalendarEvent[];
  calendar: Calendar;
  categoryStorage: CalendarEventCategory[];
  internalCalendar: CalendarDTO;

  metaDataTypeMap = new Map<string, number>();

  msCullOptions: MsOptions;
  culls: CullDTO[];
  allPlayerCulls: CullDTO[];

  eventEdit: CalendarEvent | undefined;

  inventoryAggregation: InventoryAggregationDTO | undefined;

  filteredCulls: PlayerCullDTO[];
  thresholdValues: number[] = [1, 2, 3, 4, 5, 10, 20];
  threshold: number;

  constructor(
    private api: API,
    private errorService: ErrorService,
    public permissionService: PermissionService,
    private util: UtilService,
    public modalService: ModalService,
    private navService: NavigationService,
    private commercialService: CommercialReservationService,
    private destroyRef: DestroyRef
  ) {
    this.threshold = null;
    this.events = [];
    this.commercials = [];
    this.categoryStorage = [];
    this.threshold = 2;
    this.culls = [];
  }

  ngOnInit(): void {
    this.requestDebounced();
    this.getAllPlayerCulls();
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'PLAYER_CULLS') {
      if (options) {
        this.msCullOptions = options;
      }
      if (this.msCullOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.msCullOptions?.search),
              type: 'PlayerCull',
            },
            this.msCullOptions.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (culls: CullDTO[]) => {
              this.culls = culls;
            },
            error: error => {
              this.errorService.errorHandler(error);
            },
          });
      }
    }
  }

  getNetworkData() {
    const filter = new FilterableInventoryRequestDTO();
    filter.playerCulls = this.filteredCulls as PlayerCullDTO[];
    this.api
      .ds_rm_Inventory_aggregation([filter])
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: filterData => {
          this.inventoryAggregation = filterData[0];
        },
        error: err => {
          this.errorService.errorHandler(err);
        },
      });
  }

  getAllPlayerCulls() {
    this.api
      .cs_Cull_retrieveAll(
        {
          type: 'PlayerCull',
        },
        this.pagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: culls => {
          this.allPlayerCulls = culls;
        },
        error: err => {
          this.errorService.errorHandler(err);
        },
      });
  }

  fetchCurrentCampaigns(): Promise<void> {
    return new Promise<void>(res => {
      this.api
        .ds_cm_ScheduledCampaign_retrieveAll({
          from: new SbLocalDateTime(DateTime.local(2023, 4, 1)),
          till: new SbLocalDateTime(DateTime.local(2023, 4, 30)),
        })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: campaigns => {
            if (this.filteredCulls?.length > 0) {
              this.commercials = this.filterCommercialsForFilter(campaigns);
            } else if (campaigns) {
              for (const campaign of campaigns) {
                if (campaign.commercials?.length > 0) {
                  for (const commercial of campaign.commercials) {
                    this.commercials.push(commercial as unknown as CommercialDTO);
                  }
                }
              }
            }
            this.convertCommercialsToEvents(this.commercials);
            res();
          },
          error: err => {
            this.errorService.errorHandler(err);
          },
        });
    });
  }

  fetchCalendarTypes(): Promise<void> {
    // TODO refactor
    return new Promise<void>(res => {
      this.api
        .dm_time_Calendar_retrieveAll({}, this.pagination)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: data => {
            if (data && data.length > 0) {
              this.internalCalendar = data[0];
              this.api
                .dm_time_Calendar_retrieveAllCalendarEntryTypesByCalendarType(data[0].type.id, {}, this.pagination)
                .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                .subscribe({
                  next: d => {
                    this.categoryStorage = [];
                    for (const e of d) {
                      this.categoryStorage.push(this.mapCalendarEntryTypeDTOToCalendarEventCategory(e));
                      this.mapDataTypes(e.allowedMetaDataTypes);
                    }
                    res();
                  },
                  error: err => {
                    this.errorService.errorHandler(err);
                  },
                });
            } else {
              this.api
                .dm_time_Calendar_retrieveAllCalendarTypes({ identifier: ['reservations'] })
                .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                .subscribe({
                  next: types => {
                    const calendar: CalendarFullDTO = new CalendarFullDTO();
                    calendar.name = 'Reservierungen 2023';
                    this.api
                      .dm_time_Calendar_create(types[0].id, calendar)
                      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                      .subscribe({
                        next: createdCalendar => {
                          this.internalCalendar = createdCalendar;
                          this.api
                            .dm_time_Calendar_retrieveAllCalendarEntryTypesByCalendarType(createdCalendar.type.id, {}, this.pagination)
                            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                            .subscribe({
                              next: d => {
                                this.categoryStorage = [];
                                for (const e of d) {
                                  this.categoryStorage.push(this.mapCalendarEntryTypeDTOToCalendarEventCategory(e));
                                  this.mapDataTypes(e.allowedMetaDataTypes);
                                }
                                res();
                              },
                              error: err => {
                                this.errorService.errorHandler(err);
                              },
                            });
                        },
                        error: err => {
                          this.errorService.errorHandler(err);
                        },
                      });
                  },
                  error: err => {
                    this.errorService.errorHandler(err);
                  },
                });
            }
          },
          error: err => {
            this.errorService.errorHandler(err);
          },
        });
    });
  }

  generateDescription(meta: any): string {
    if (meta?.value) {
      for (const cull of this.allPlayerCulls) {
        // tslint:disable-next-line:triple-equals
        if (cull.id == meta.value) {
          return 'Gerätefilter: ' + cull.name;
        }
      }
    }
    return null;
  }

  update($event: PlayerCullDTO[], type: string) {
    if (type === 'PLAYER_CULLS') {
      if ($event && $event?.length > 0) {
        this.filteredCulls = $event;
      } else {
        this.filteredCulls = [];
      }
    }
    this.requestDebounced();
  }

  requestDebounced() {
    this.refreshFromServer();
    this.getNetworkData();
  }

  refreshFromServer() {
    this.fetchCurrentCampaigns().then(() => {
      this.fetchCalendarTypes().then(() => {
        this.fetchCalendarEntries().then(() => {
          this.calendar = new Calendar({
            name: 'Reservierungen',
            dateRange: new DateRange({ from: DateTime.now(), till: DateTime.now() }),
            selectedDate: DateTime.now().startOf('day'),
            viewMode: CalendarViewMode.MonthView,
            events: this.events,
            categories: this.categoryStorage,
          });
        });
      });
    });
  }

  mapDataTypes(dataTypes: any[]) {
    for (const dataType of dataTypes) {
      this.metaDataTypeMap.set(dataType.name, dataType.id);
    }
  }

  fetchCalendarEntries(): Promise<void> {
    return new Promise<void>(res => {
      this.api
        .dm_time_CalendarEntry_retrieveAll({}, this.pagination)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: entries => {
            this.convertEntriesToEvents(entries);
            res();
          },
          error: err => {
            this.errorService.errorHandler(err);
          },
        });
    });
  }

  checkInFilteredCulls(playerCullId: number): boolean {
    for (const filter of this.filteredCulls) {
      if (filter.id === playerCullId) {
        return true;
      }
    }
    return false;
  }

  filterCommercialsForFilter(campaigns: ScheduledCampaignDTO[]): CommercialDTO[] {
    const commercials: CommercialDTO[] = [];
    if (!campaigns) {
      return commercials;
    }

    for (const campaign of campaigns) {
      if (!campaign?.playerCull || !this.checkInFilteredCulls(campaign.playerCull.id) || !campaign.commercials) {
        continue;
      }
      for (const commercial of campaign.commercials) {
        if (commercial?.playerCull) {
          if (this.checkInFilteredCulls(commercial.playerCull.id)) {
            commercials.push(commercial as unknown as CommercialDTO);
          }
        } else {
          commercials.push(commercial as unknown as CommercialDTO);
        }
      }
    }
    return commercials;
  }

  mapCalendarEntryTypeDTOToCalendarEventCategory(entry: CalendarEntryTypeDTO): CalendarEventCategory {
    const eventCat: CalendarEventCategory = new CalendarEventCategory({
      id: entry.id,
      name: entry.name,
      icon: entry.icon,
      color: '#' + entry.color,
    });
    // needs separation for object creation
    return eventCat;
  }

  convertCommercialsToEvents(commercials: CommercialDTO[]) {
    this.events = [];
    for (const commercial of commercials) {
      const calendarEvent: CalendarEvent = new CalendarEvent({
        name: commercial.name,
        dateRange: new DateRange({
          from: commercial.from.value,
          till: commercial.till.value,
        }),
        category: {
          name: 'Gebucht',
          icon: 'commercial',
          color: '#949494',
        },
        readonly: true,
        description: commercial.playerCull ? 'Gerätefilter: ' + commercial.playerCull.name : null,
      });
      this.events.push(calendarEvent);
    }
  }

  filterEntriesForPlayerCull(entry: CalendarEntryFullDTO): boolean {
    if (!this.filteredCulls || this.filteredCulls?.length <= 0) {
      return true;
    }
    if (!entry?.metaData || entry.metaData.length > 0) {
      return false;
    }
    for (const data of entry.metaData) {
      if (data.id === this.metaDataTypeMap.get('playerCull')) {
        for (const cull of this.filteredCulls) {
          if (data.value === cull.id.toString()) {
            return true;
          }
        }
      }
    }
    return false;
  }

  convertEntriesToEvents(entries: CalendarEntryFullDTO[]) {
    if (!entries) {
      return;
    }
    if (entries?.length > 0) {
      this.internalCalendar = entries[0].calendar;
    }
    for (const entry of entries) {
      if (this.filterEntriesForPlayerCull(entry)) {
        if (entry.validity[0]) {
          const calendarEvent: CalendarEvent = new CalendarEvent({
            id: entry.id,
            name: entry.name,
            dateRange: new DateRange({
              from: entry.validity[0].from.value,
              till: entry.validity[0].till.value,
            }),
            category: this.getEventCategory(entry.type.id),
            description: this.generateDescription(entry.metaData[0]),
          });
          this.events.push(calendarEvent);
        }
      }
    }
  }

  getEventCategory(entryId: number): CalendarEventCategory {
    for (const cat of this.categoryStorage) {
      if (cat.id === entryId) {
        return cat;
      }
    }
    return;
  }

  retrieveOutput($event: any) {
    const type = $event.type;
    const event = $event.event;
    const entry = new CalendarEntryFullDTO();
    entry.name = event.name;
    entry.startDate = new SbLocalDate({
      year: event.dateRange.from.get('year'),
      month: event.dateRange.from.get('month'),
      day: event.dateRange.from.get('day'),
    });
    entry.endDate = new SbLocalDate({
      year: event.dateRange.till.get('year'),
      month: event.dateRange.till.get('month'),
      day: event.dateRange.till.get('day'),
    });
    entry.startTime = new SbLocalTime({ hour: event.dateRange.from.get('hour'), minute: event.dateRange.from.get('minute'), second: 0 });
    entry.endTime = new SbLocalTime({ hour: event.dateRange.till.get('hour'), minute: event.dateRange.till.get('minute'), second: 59 });
    entry.metaData = [];
    if (this.filteredCulls && this.filteredCulls.length > 0) {
      for (const cull of this.filteredCulls) {
        const meta = new CalendarEntryMetaDataDTO();
        meta.value = cull.id;
        meta.id = this.metaDataTypeMap.get('playerCull');
        entry.metaData.push(meta);
      }
    }
    event.description = this.generateDescription(entry.metaData[0]);
    this.eventEdit = null;
    if (type === 'create') {
      if (this.internalCalendar) {
        this.api
          .dm_time_Calendar_createCalendarEntry(this.internalCalendar.id, event.category.id, entry)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              event.id = data.id;
              this.eventEdit = event;
            },
            error: err => {
              this.errorService.errorHandler(err);
            },
          });
      }
    }
    if (type === 'edit') {
      entry.id = event.id;
      this.api
        .dm_time_CalendarEntry_changeEntryType(entry.id, event.category.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: changedEntry => {
            this.api
              .dm_time_CalendarEntry_update(changedEntry)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: calendarEntry => {
                  event.id = calendarEntry.id;
                  this.eventEdit = event;
                },
                error: err => {
                  this.errorService.errorHandler(err);
                },
              });
          },
          error: err => {
            this.errorService.errorHandler(err);
          },
        });
    }
    if (type === 'delete') {
      entry.id = event.id;
      this.api
        .dm_time_CalendarEntry_deleteById(entry.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {},
          error: err => {
            this.errorService.errorHandler(err);
          },
        });
    }
  }

  createNewCalendarEvent(calendarEntry: CalendarEntryFullDTO): CalendarEvent[] {
    return [
      new CalendarEvent({
        name: calendarEntry.name,
        dateRange: new DateRange({
          from: DateTime.local(
            calendarEntry.validity[0].from.value.year,
            calendarEntry.validity[0].from.value.month,
            calendarEntry.validity[0].from.value.day
          ),
          till: DateTime.local(
            calendarEntry.validity[0].till.value.year,
            calendarEntry.validity[0].till.value.month,
            calendarEntry.validity[0].till.value.day
          ),
        }),
        color: 'red',
      }),
    ];
  }

  migrateToCommercial(entry: CalendarEntryDTO) {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: CampaignsListComponent,
          data: {
            modalType: 'reservation',
          },
        },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: any) => {
        if (result?.campaign) {
          this.commercialService.entry = entry;
          this.commercialService.playerCull = this.filteredCulls[0] ? this.filteredCulls[0] : null;
          this.navService.navigateToPoint(RoutingID.commercialsCreate, [
            {
              id: 'campaignID',
              value: result.campaign.id,
            },
          ]);
        }
      })
      .catch(() => {
        /* needs to be function or else error in console */
      });
  }

  lookupEvent($event: number) {
    this.api
      .dm_time_CalendarEntry_retrieveById($event)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (calendarEntry: CalendarEntryDTO) => {
          this.migrateToCommercial(calendarEntry);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }
}
